import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { useRef } from 'react';

import {
  mapShapeDestroy,
  mapShapeCreate,
  fetchMap,
  mapShapeGroupDestroy,
  mapShapeGroupCreate,
  mapShapesSet,
} from 'graphql/queries';

const useBoundariesSelectService = (pageId) => {
  const client = useApolloClient();
  const recentlyRemovedMapShapeIdsByShapeId = useRef({});
  const fetchMapVariables = { id: pageId };
  const [addShape] = useMutation(mapShapeCreate);
  const [destroyShape] = useMutation(mapShapeDestroy);
  const [createShapeGroup] = useMutation(mapShapeGroupCreate);
  const [destroyShapeGroup] = useMutation(mapShapeGroupDestroy);
  const [setShapesIds] = useMutation(mapShapesSet);

  const readMapFromCache = () => {
    try {
      return client.readQuery({ query: fetchMap, variables: fetchMapVariables });
    } catch (error) {
      return null;
    }
  };

  const writeMapToCache = (updater) => {
    const cachedMapData = readMapFromCache();

    if (!cachedMapData || !cachedMapData.map) {
      return;
    }

    const nextMap = updater(cachedMapData.map);

    client.writeQuery({
      query: fetchMap,
      variables: fetchMapVariables,
      data: {
        ...cachedMapData,
        map: nextMap,
      },
    });
  };

  const writeMapShapesToCache = (updater) => {
    writeMapToCache((currentMap) => {
      const nextMapShapes = updater(currentMap.mapShapes || []);

      return {
        ...currentMap,
        mapShapes: nextMapShapes,
      };
    });
  };

  const remapSubMapShapes = (subMapShapes, previousMapShapeId, nextMapShapeId) => {
    if (!Array.isArray(subMapShapes)) {
      return subMapShapes;
    }

    return subMapShapes.map((shapeTotal) => {
      if (Number(shapeTotal.id) !== Number(previousMapShapeId)) {
        return shapeTotal;
      }

      return {
        ...shapeTotal,
        id: String(nextMapShapeId),
      };
    });
  };

  const remapAggregateTotalsForShape = (totals, previousMapShapeId, nextMapShapeId) => {
    if (!totals) {
      return totals;
    }

    const mapShapes = Array.isArray(totals.mapShapes)
      ? totals.mapShapes.map((shapeTotal) => {
        if (Number(shapeTotal.id) !== Number(previousMapShapeId)) {
          return shapeTotal;
        }

        return {
          ...shapeTotal,
          id: String(nextMapShapeId),
        };
      })
      : totals.mapShapes;

    const mapDatasets = Array.isArray(totals.mapDatasets)
      ? totals.mapDatasets.map(mapDatasetTotal => ({
        ...mapDatasetTotal,
        subMapShapes: remapSubMapShapes(
          mapDatasetTotal.subMapShapes,
          previousMapShapeId,
          nextMapShapeId,
        ),
      }))
      : totals.mapDatasets;

    return {
      ...totals,
      mapShapes,
      mapDatasets,
    };
  };

  const refreshMapInBackground = () => {
    client.query({
      query: fetchMap,
      variables: fetchMapVariables,
      fetchPolicy: 'network-only',
    }).catch(() => {});
  };

  const AddBounds = (id) => {
    if (!id) {
      return Promise.resolve();
    }

    return addShape({
      variables: {
        input: {
          mapId: Number(pageId),
          shapeId: Number(id),
        },
      },
    }).then(({ data }) => {
      const createdMapShape = data && data.mapShapeCreate && data.mapShapeCreate.mapShape;

      if (createdMapShape) {
        const createdShapeId = Number(createdMapShape.shape.id);
        const previousMapShapeId = recentlyRemovedMapShapeIdsByShapeId.current[createdShapeId];

        writeMapShapesToCache(currentMapShapes => (
          currentMapShapes.some(
            ({ shape }) => Number(shape.id) === Number(createdMapShape.shape.id),
          )
            ? currentMapShapes
            : [...currentMapShapes, createdMapShape]
        ));

        if (previousMapShapeId) {
          writeMapToCache((currentMap) => {
            const nextAggregates = Array.isArray(currentMap.aggregates)
              ? currentMap.aggregates.map(aggregate => ({
                ...aggregate,
                totals: remapAggregateTotalsForShape(
                  aggregate.totals,
                  previousMapShapeId,
                  createdMapShape.id,
                ),
              }))
              : currentMap.aggregates;

            return {
              ...currentMap,
              aggregates: nextAggregates,
            };
          });

          delete recentlyRemovedMapShapeIdsByShapeId.current[createdShapeId];
        }
      }

      refreshMapInBackground();
    });
  };

  const RemoveBounds = (id) => {
    if (!id) {
      return Promise.resolve();
    }

    const deletedShapeId = Number(id);

    const cachedMapData = readMapFromCache();
    const cachedMapShapes = (
      cachedMapData
      && cachedMapData.map
      && Array.isArray(cachedMapData.map.mapShapes)
    ) ? cachedMapData.map.mapShapes : [];
    const removedMapShape = cachedMapShapes.find(
      ({ shape }) => Number(shape.id) === deletedShapeId,
    );

    if (removedMapShape) {
      recentlyRemovedMapShapeIdsByShapeId.current[deletedShapeId] = Number(removedMapShape.id);
    }

    writeMapShapesToCache(currentMapShapes => (
      currentMapShapes.filter(({ shape }) => Number(shape.id) !== deletedShapeId)
    ));

    return destroyShape({
      variables: {
        input: {
          mapId: pageId,
          shapeId: id,
        },
      },
    }).then(() => {
      refreshMapInBackground();
    });
  };

  const AddShapeGroup = (id) => {
    if (!id) {
      return Promise.resolve();
    }

    return createShapeGroup({
      variables: {
        input: {
          mapId: pageId,
          shapeGroupId: id,
        },
      },
    }).then(({ data }) => {
      const createdMapShapes = (
        data
        && data.mapShapeGroupCreate
        && data.mapShapeGroupCreate.mapShapes
      ) ? data.mapShapeGroupCreate.mapShapes : [];

      if (createdMapShapes.length) {
        writeMapShapesToCache((currentMapShapes) => {
          const currentShapeIds = currentMapShapes.map(({ shape }) => Number(shape.id));
          const nextMapShapes = createdMapShapes.filter(
            ({ shape }) => !currentShapeIds.includes(Number(shape.id)),
          );

          return [...currentMapShapes, ...nextMapShapes];
        });
      }

      refreshMapInBackground();
    });
  };

  const RemoveShapeGroup = (id) => {
    if (!id) {
      return Promise.resolve();
    }

    const deletedShapeGroupId = Number(id);

    writeMapShapesToCache(currentMapShapes => (
      currentMapShapes.filter(
        ({ shape }) => Number(shape.shapeGroup && shape.shapeGroup.id) !== deletedShapeGroupId,
      )
    ));

    return destroyShapeGroup({
      variables: {
        input: {
          mapId: pageId,
          shapeGroupId: id,
        },
      },
    }).then(() => {
      refreshMapInBackground();
    });
  };

  const MapShapesSet = (ids) => {
    if (!ids) {
      return Promise.resolve();
    }

    const requestedShapeIds = ids.map(Number);

    writeMapShapesToCache(currentMapShapes => (
      currentMapShapes.filter(({ shape }) => requestedShapeIds.includes(Number(shape.id)))
    ));

    return setShapesIds({
      variables: {
        input: {
          mapId: Number(pageId),
          shapeIds: ids,
        },
      },
    }).then(({ data }) => {
      const nextMapShapes = data && data.mapShapesSet && data.mapShapesSet.mapShapes
        ? data.mapShapesSet.mapShapes
        : null;

      if (nextMapShapes) {
        writeMapShapesToCache(() => nextMapShapes);
      }

      refreshMapInBackground();
    });
  };

  return {
    AddBounds,
    RemoveBounds,
    AddShapeGroup,
    RemoveShapeGroup,
    MapShapesSet,
  };
};

export default useBoundariesSelectService;
